// frontend/src/components/dashboard/orders/OrderList.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Search,
  Visibility,
  Print,
  CheckCircle,
  Cancel,
  FilterList,
  Error as ErrorIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import ReceiptDialog from '../pos/dialogs/ReceiptDialog';
import DashboardContext from '../contexts/DashboardContext';
import menuService from '../services/menuService';

const OrderList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const MotionDiv = motion.div;
  const { vendorInfo } = useContext(DashboardContext);
  
  // State for orders data
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Row expand state
  const [expandedRows, setExpandedRows] = useState({});
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Receipt dialog state
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  
  // Error state
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    field: 'created_at', // Default sort field is created_at (date)
    direction: 'desc'    // Default direction is descending (newest first)
  });

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: 
        prevConfig.field === field && prevConfig.direction === 'desc' 
          ? 'asc' 
          : 'desc'
    }));
  };
  
  // Fetch orders on component mount
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      
      // Add first debug log before API call
      console.log('🔄 Fetching orders with params:', {
        method: 'getFullOrderDetails',
        timestamp: new Date().toISOString()
      });
      
      try {
        const response = await menuService.getFullOrderDetails();
        console.log("Fetched orders:", response);
        setOrders(response);
        setError(null);
        
        // Add second debug log after setting orders
        console.log('📋 Received orders:', response.length);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setError(t('orders.loadError'));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOrders();
  }, [t]);
  
  // Apply filters whenever they change
  useEffect(() => {
  let filtered = [...orders];
  
  // Apply search filter on order ID
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(order => 
      order.order.order_id.toString().includes(term)
    );
  }
  
  // Apply status filter
  if (statusFilter !== 'all') {
    filtered = filtered.filter(order => 
      order.order.status.toLowerCase() === statusFilter.toLowerCase()
    );
  }
  
  // Apply date range filter - Fixed to be inclusive of both start and end dates
  if (startDate && endDate) {
    filtered = filtered.filter(order => {
      const orderDate = new Date(order.order.created_at);
      
      // Create start of day for start date and end of day for end date
      const filterStartDate = new Date(startDate);
      filterStartDate.setHours(0, 0, 0, 0);
      
      const filterEndDate = new Date(endDate);
      filterEndDate.setHours(23, 59, 59, 999);
      
      return orderDate >= filterStartDate && orderDate <= filterEndDate;
    });
  } else if (startDate) {
    // Only start date is selected
    filtered = filtered.filter(order => {
      const orderDate = new Date(order.order.created_at);
      const filterStartDate = new Date(startDate);
      filterStartDate.setHours(0, 0, 0, 0);
      
      return orderDate >= filterStartDate;
    });
  } else if (endDate) {
    // Only end date is selected
    filtered = filtered.filter(order => {
      const orderDate = new Date(order.order.created_at);
      const filterEndDate = new Date(endDate);
      filterEndDate.setHours(23, 59, 59, 999);
      
      return orderDate <= filterEndDate;
    });
  }
  
  // Apply payment method filter
  if (paymentMethodFilter !== 'all') {
    // Add debugging to see what's happening
    console.log('Filtering by payment method:', paymentMethodFilter);
    console.log('Payment methods in data:', [...new Set(filtered.map(order => order.payment?.method))]);
    
    filtered = filtered.filter(order => {
      // Get the actual payment method from the order
      const orderMethod = order.payment?.method?.toLowerCase() || '';
      
      // Handle different payment method formats
      switch (paymentMethodFilter.toLowerCase()) {
        case 'cash':
          return /cash|exact_cash|calculate_change/i.test(orderMethod);
        case 'card':
          return /card|credit|debit|visa|mastercard/i.test(orderMethod);
        case 'qr':
          return /qr|qr_code|qr_payment/i.test(orderMethod);
        default:
          return orderMethod === paymentMethodFilter.toLowerCase();
      }
    });
  }

  // FIXED: Apply sorting regardless of filters (moved outside conditional blocks)
  filtered.sort((a, b) => {
    // Handle different field types appropriately
    switch(sortConfig.field) {
      case 'order_id':
        return sortConfig.direction === 'asc' 
          ? a.order.order_id - b.order.order_id 
          : b.order.order_id - a.order.order_id;
      case 'created_at':
        return sortConfig.direction === 'asc' 
          ? new Date(a.order.created_at) - new Date(b.order.created_at)
          : new Date(b.order.created_at) - new Date(a.order.created_at);
      case 'total_amount':
        return sortConfig.direction === 'asc' 
          ? parseFloat(a.order.total_amount) - parseFloat(b.order.total_amount)
          : parseFloat(b.order.total_amount) - parseFloat(a.order.total_amount);
      case 'status':
        return sortConfig.direction === 'asc'
          ? a.order.status.localeCompare(b.order.status)
          : b.order.status.localeCompare(a.order.status);
      default:
        return 0;
    }
  });
  
  setFilteredOrders(filtered);
  setPage(0); // Reset to first page when filters change
}, [orders, searchTerm, statusFilter, startDate, endDate, paymentMethodFilter, sortConfig]); // FIXED: Added sortConfig to dependencies

  
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle status filter change
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };
  
  // Handle payment method filter change
  const handlePaymentMethodChange = (e) => {
    setPaymentMethodFilter(e.target.value);
  };
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle view receipt
  const handleViewReceipt = (order) => {
    setSelectedOrder(order);
    setReceiptOpen(true);
  };

  // Handle print receipt
  const handlePrintReceipt = (order) => {
    // Set selected order and open dialog - printing will be handled in the dialog
    setSelectedOrder(order);
    setReceiptOpen(true);
    // We'll trigger print automatically after dialog opens
  };

  // Toggle row expansion
  const toggleRowExpansion = (orderId) => {
    setExpandedRows(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };
  
  // Handle update order status
  const handleUpdateStatus = async (order, newStatus) => {
    try {
      // Call the API
      await menuService.updateOrderStatus(order.order.order_id, newStatus);
      
      // Update local state
      const updatedOrders = orders.map(o => 
        o.order.order_id === order.order.order_id 
          ? { ...o, order: { ...o.order, status: newStatus } } 
          : o
      );
      
      setOrders(updatedOrders);
      alert(t('orders.statusUpdateSuccess', { orderId: order.order.order_id, status: newStatus }));
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(t('orders.statusUpdateError'));
    }
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setStartDate(null);
    setEndDate(null);
    setPaymentMethodFilter('all');
  };
  
  // Get status chip color based on status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return {
          bgcolor: theme.palette.success.light + '20',
          color: theme.palette.success.main
        };
      case 'void':
        return {
          bgcolor: theme.palette.warning.light + '20',
          color: theme.palette.warning.main
        };
      case 'cancelled':
      case 'canceled':
        return {
          bgcolor: theme.palette.error.light + '20',
          color: theme.palette.error.main
        };
      default:
        return {
          bgcolor: theme.palette.grey[100],
          color: theme.palette.grey[700]
        };
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy h:mm a');
    } catch {
      // Empty catch block without parameter
      return dateString;
    }
  };
  
  // Get appropriate status icon
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle fontSize="small" />;
      case 'void':
        return <ErrorIcon fontSize="small" />;
      case 'cancelled':
      case 'canceled':
        return <Cancel fontSize="small" />;
      default:
        return null;
    }
  };
  
  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {t('orders.listTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('orders.listSubtitle')}
        </Typography>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  placeholder={t('orders.searchPlaceholder')}
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('orders.status')}</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusChange}
                    label={t('orders.status')}
                  >
                    <MenuItem value="all">{t('orders.allStatuses')}</MenuItem>
                    <MenuItem value="completed">{t('orders.completed')}</MenuItem>
                    <MenuItem value="void">{t('orders.void')}</MenuItem>
                    <MenuItem value="cancelled">{t('orders.cancelled')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('orders.paymentMethod')}</InputLabel>
                  <Select
                    value={paymentMethodFilter}
                    onChange={handlePaymentMethodChange}
                    label={t('orders.paymentMethod')}
                  >
                    <MenuItem value="all">{t('orders.allMethods')}</MenuItem>
                    <MenuItem value="cash">{t('orders.cash')}</MenuItem>
                    <MenuItem value="card">{t('orders.card')}</MenuItem>
                    <MenuItem value="qr">{t('orders.qr')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label={t('orders.fromDate')}
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setStartDate(new Date(e.target.value));
                    } else {
                      setStartDate(null);
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label={t('orders.toDate')}
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setEndDate(new Date(e.target.value));
                    } else {
                      setEndDate(null);
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={1}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="medium"
                  onClick={handleClearFilters}
                  startIcon={<FilterList />}
                >
                  {t('orders.clear')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography>{t('orders.loading')}</Typography>
              </Box>
            ) : error ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2, opacity: 0.7 }} />
                <Typography variant="h6" gutterBottom color="error">
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  sx={{ mt: 2 }}
                >
                  {t('orders.retry')}
                </Button>
              </Box>
            ) : filteredOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography>{t('orders.noOrdersFound')}</Typography>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{ mt: 2 }}
                >
                  {t('orders.clearFilters')}
                </Button>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell 
                          onClick={() => handleSort('order_id')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('orders.table.orderId')}
                            {sortConfig.field === 'order_id' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('created_at')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('orders.table.dateTime')}
                            {sortConfig.field === 'created_at' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell 
                          onClick={() => handleSort('status')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('orders.table.status')}
                            {sortConfig.field === 'status' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{t('orders.table.items')}</TableCell>
                        <TableCell>{t('orders.table.device')}</TableCell>
                        <TableCell>{t('orders.table.payment')}</TableCell>
                        <TableCell 
                          onClick={() => handleSort('total_amount')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('orders.table.total')}
                            {sortConfig.field === 'total_amount' && (
                              sortConfig.direction === 'asc' ? 
                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> : 
                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{t('orders.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredOrders
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((order) => (
                          <React.Fragment key={order.order.order_id}>
                            <TableRow hover>
                              <TableCell padding="checkbox">
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRowExpansion(order.order.order_id)}
                                >
                                  {expandedRows[order.order.order_id] ? (
                                    <KeyboardArrowUp fontSize="small" />
                                  ) : (
                                    <KeyboardArrowDown fontSize="small" />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>
                                  #{order.order.order_id}
                                </Typography>
                              </TableCell>
                              <TableCell>{formatDate(order.order.created_at)}</TableCell>
                              <TableCell>
                                <Chip
                                  icon={getStatusIcon(order.order.status)}
                                  label={order.order.status}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    ...getStatusColor(order.order.status)
                                  }}
                                />
                              </TableCell>
                              <TableCell>{order.items.length}</TableCell>
                              <TableCell>{order.order.device_number || t('common.notAvailable')}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{order.payment.method}</TableCell>
                              <TableCell>
                                <Typography fontWeight={500}>
                                  ${parseFloat(order.order.total_amount).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Tooltip title={t('orders.viewReceipt')}>
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={() => handleViewReceipt(order)}
                                    >
                                      <Visibility fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('orders.printReceipt')}>
                                    <IconButton
                                      color="secondary"
                                      size="small"
                                      onClick={() => handlePrintReceipt(order)}
                                    >
                                      <Print fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                            {expandedRows[order.order.order_id] && (
                              <TableRow>
                                <TableCell colSpan={9} sx={{ p: 0 }}>
                                  <Box
                                    sx={{
                                      py: 2,
                                      px: 3,
                                      backgroundColor: 'action.hover',
                                    }}
                                  >
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <Box>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {t('orders.orderDetails')}
                                          </Typography>
                                          <Box sx={{ display: 'flex', mt: 1 }}>
                                            <Box sx={{ mr: 4 }}>
                                              <Typography variant="body2" color="text.secondary">
                                                {t('orders.paymentMethod')}
                                              </Typography>
                                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                {order.payment.method}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ mr: 4 }}>
                                              <Typography variant="body2" color="text.secondary">
                                                {t('orders.deviceNumber')}
                                              </Typography>
                                              <Typography variant="body2">
                                                {order.order.device_number || t('common.notAvailable')}
                                              </Typography>
                                            </Box>
                                            <Box>
                                              <Typography variant="body2" color="text.secondary">
                                                {t('orders.cashier')}
                                              </Typography>
                                              <Typography variant="body2">
                                                {order.account.username}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                                          <Box sx={{ mr: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                              {t('orders.changeStatus')}:
                                            </Typography>
                                          </Box>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="success"
                                            onClick={() => handleUpdateStatus(order, 'completed')}
                                            disabled={order.order.status === 'completed'}
                                            sx={{ ml: 1 }}
                                          >
                                            {t('orders.completed')}
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => handleUpdateStatus(order, 'void')}
                                            disabled={order.order.status === 'void'}
                                            sx={{ ml: 1 }}
                                          >
                                            {t('orders.void')}
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleUpdateStatus(order, 'cancelled')}
                                            disabled={order.order.status === 'cancelled'}
                                            sx={{ ml: 1 }}
                                          >
                                            {t('orders.cancel')}
                                          </Button>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredOrders.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </CardContent>
        </Card>
      </MotionDiv>
      
      {/* Receipt Dialog */}
      <ReceiptDialog
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        order={selectedOrder}
        vendorInfo={vendorInfo}
      />
    </Box>
  );
};

export default OrderList;